import { MasterDataItem } from "@/hooks/useMasterData";
import { EXPERT_SIGNUP_ROLE_PARAM } from "@/utils/roleUtils";
import { formatDisplayDate } from "@/utils/formatDisplayDate";

const _APP_ENV = import.meta.env.VITE_APP_ENV as string;

export const APP_BASE_URLS: Record<string, string> = {
  local: "http://localhost:5173",
  dev: "https://achieveos-dev.forwardshare.co",
  stg: "https://achieveos-stg.forwardshare.co",
  prod: "https://achieveos.forwardshare.co",
};

/**
 * Base URL for the current environment.
 */
export const APP_BASE_URL: string = APP_BASE_URLS[_APP_ENV] ?? APP_BASE_URLS.prod;

/**
 * Base invite URL for the current environment.
 * Append `&ref={userId}` to create a unique per-advisor invite link.
 */
export const INVITE_BASE_URL: string = `${APP_BASE_URL}/signup?role=${EXPERT_SIGNUP_ROLE_PARAM}`;

/**
 * The product name as shown to users: page titles, headings, body copy, alt
 * text and generated PDF reports.
 *
 * Read from VITE_APP_NAME with the environment suffix stripped — that var
 * carries "AchieveOS (Dev)" / "AchieveOS (Staging)" so builds are
 * distinguishable, but prose like "Welcome to AchieveOS" must not inherit the
 * suffix. The backend keeps its own copy in `app/core/constants.py`
 * (BRAND_NAME) for email subjects and templates; change the two together.
 */
export const BRAND_NAME: string = (import.meta.env.VITE_APP_NAME as string | undefined)
  ?.replace(/\s*\(.*\)\s*$/, "")
  .trim() || "AchieveOS";

/**
 * The single account allowed to sign in with a password instead of the magic
 * link. When the login form's email matches this (case-insensitively), the UI
 * shows a password field and calls POST /auth/login/password; every other email
 * keeps the passwordless flow. Must match the backend's REVIEW_PASSWORD_LOGIN_EMAIL.
 */
export const REVIEW_PASSWORD_LOGIN_EMAIL: string = (
  (import.meta.env.VITE_REVIEW_PASSWORD_LOGIN_EMAIL as string | undefined) ||
  "skantem@forwardshare.co"
)
  .trim()
  .toLowerCase();

export const isReviewPasswordEmail = (email: string): boolean =>
  !!email && email.trim().toLowerCase() === REVIEW_PASSWORD_LOGIN_EMAIL;

export function getEnvVar(key: keyof Environment["env"], byPass?: boolean): string {
  const actualKey = `VITE_${key}`;
  if (!import.meta.env[actualKey]) {
    if (byPass) {
      return "";
    }
    throw new Error(`Environment variable ${key} is not set`);
  }

  return import.meta.env[actualKey];
}

export function parseMasterData(masterData: never[] | MasterDataItem[] | undefined) {
  if (!masterData) return [];

  const options = masterData.map((item) => ({
    label: item.optionName,
    value: item.optionValue,
  }));

  const otherOption = options.find((opt) => opt.label.toLowerCase() === "other");

  const sortedOptions = options
    .filter((opt) => opt !== otherOption)
    .sort((a, b) => a.label.localeCompare(b.label));

  return otherOption ? [...sortedOptions, otherOption] : sortedOptions;
}

/**
 * Parse advisory-style options (from parseMasterData) into title + description.
 * Labels like "Title (description)" are split; otherwise description is empty.
 * Filters out "No Preference" option.
 * Reused by AdvisoryStyleCard, ViewAdvisorProfilePage, and preference forms.
 */
export function parseAdvisoryStyleOptions(
  options: { label: string; value: string }[],
): { value: string; label: string; title: string; description: string }[] {
  return options
    .filter((style) => style.label.toLowerCase().trim() !== "no preference")
    .map((style) => {
      const label = style.label ?? "";
      const openParenIndex = label.indexOf("(");
      const lastCloseParenIndex = label.lastIndexOf(")");
      if (
        openParenIndex !== -1 &&
        lastCloseParenIndex !== -1 &&
        lastCloseParenIndex > openParenIndex
      ) {
        return {
          ...style,
          title: label.substring(0, openParenIndex).trim(),
          description: label.substring(openParenIndex + 1, lastCloseParenIndex).trim(),
        };
      }
      return { ...style, title: label, description: "" };
    });
}

export function padZero(num: number, digits: number = 2): string {
  return num.toString().padStart(digits, "0");
}

export function parseArraytoOptions(optionsList: never[] | string[] | undefined) {
  if (!optionsList) return [];
  return optionsList.map((item) => ({
    label: item,
    value: item,
  }));
}

export const getInitials = (name: string) => {
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
};

export const formatDate = (dateString: string | null) => {
  if (!dateString) return "—";
  return formatDisplayDate(dateString, { variant: "date", fallback: dateString });
};

/**
 * Formats an active period date range into a readable string
 * @param activeFrom - Start date string
 * @param activeTill - End date string
 * @returns Formatted date range string, e.g., "(Jan 15, 2024 - Feb 20, 2024)" or empty string if invalid
 */
export const formatActivePeriod = (activeFrom?: string, activeTill?: string): string => {
  const from = formatDisplayDate(activeFrom, { variant: "date" });
  const till = formatDisplayDate(activeTill, { variant: "date" });
  return from && till ? `(${from} - ${till})` : "";
};

/**
 * Formats a PAB period into a range label
 * @example "Advisory Board - Oct 1, 2025 to Dec 30, 2025"
 * @example "Advisory Board - Oct 1, 2025"
 */
export const formatPabPeriod = (activeFrom?: string, activeTill?: string): string => {
  const from = formatDisplayDate(activeFrom, { variant: "date" });
  const till = formatDisplayDate(activeTill, { variant: "date" });
  if (!from) return "";
  return till ? `Advisory Board - ${from} to ${till}` : `Advisory Board - ${from}`;
};

/**
 * Splits a delimited string into an array of trimmed, non-empty values
 * @param value - The string to split
 * @param delimiter - The delimiter to split on
 * @returns Array of trimmed, non-empty strings
 */
export const splitDelimitedString = (value: string, delimiter: string): string[] => {
  if (!value?.trim()) return [];
  return value
    .split(delimiter)
    .map((item) => item.trim())
    .filter(Boolean);
};

/**
 * Pre-normalizes master data options for efficient lookups
 * @param options - Array of MasterDataItem objects
 * @returns Normalized lookup object with lowercase keys
 */
export const createNormalizedLookup = (options: MasterDataItem[]): Record<string, string> => {
  const lookup: Record<string, string> = {};

  options.forEach((option) => {
    if (option.optionName && option.optionValue) {
      const normalizedKey = option.optionName.toLowerCase().trim();
      lookup[normalizedKey] = option.optionValue;
    }
  });

  return lookup;
};

/**
 * Pre-normalizes select options for efficient lookups (for parsed master data)
 * @param options - Array of SelectOption objects (label/value pairs)
 * @returns Normalized lookup object with lowercase keys
 */
export const createSelectOptionLookup = (
  options: { label: string; value: string }[],
): Record<string, string> => {
  const lookup: Record<string, string> = {};

  options.forEach((option) => {
    const labelLower = option.label.toLowerCase().trim();
    const valueLower = option.value.toLowerCase().trim();

    lookup[labelLower] = option.value;
    lookup[valueLower] = option.value;

    if (labelLower.includes(" ")) {
      lookup[labelLower.replace(/\s+/g, "")] = option.value;
      lookup[labelLower.replace(/\s+/g, "-")] = option.value;
    }
  });

  return lookup;
};

/**
 * Finds matching options using pre-normalized lookup with fuzzy matching
 * @param searchValues - Array of values to search for
 * @param normalizedLookup - Pre-normalized lookup object
 * @returns Array of matching option values
 */
export const findMatchingOptions = (
  searchValues: string[],
  normalizedLookup: Record<string, string>,
): string[] => {
  const matches: string[] = [];
  const seen = new Set<string>();

  searchValues.forEach((searchValue) => {
    const searchLower = searchValue.toLowerCase().trim();

    let match = normalizedLookup[searchLower];

    if (!match) {
      for (const [key, value] of Object.entries(normalizedLookup)) {
        if (key.startsWith(searchLower) || searchLower.startsWith(key)) {
          match = value;
          break;
        }
      }
    }

    if (!match) {
      for (const [key, value] of Object.entries(normalizedLookup)) {
        if (key.includes(searchLower) || searchLower.includes(key)) {
          match = value;
          break;
        }
      }
    }

    if (match && !seen.has(match)) {
      seen.add(match);
      matches.push(match);
    }
  });

  return matches;
};

/**
 * Validates a URL to ensure it uses a safe protocol (https: or http:)
 * Prevents XSS attacks from javascript: URIs or other dangerous protocols
 * @param url - The URL string to validate
 * @returns The validated URL if safe, null otherwise
 */
export const validateSafeUrl = (url: string | null | undefined): string | null => {
  if (!url || typeof url !== "string" || url.trim() === "") {
    return null;
  }

  try {
    // Parse the URL to check its protocol
    const urlObj = new URL(url.trim());
    const protocol = urlObj.protocol.toLowerCase();

    // Only allow http: and https: protocols
    if (protocol === "https:" || protocol === "http:") {
      return urlObj.toString();
    }

    // Reject javascript:, data:, file:, and other dangerous protocols
    return null;
  } catch {
    // If URL parsing fails, it's not a valid URL
    return null;
  }
};
